import { useTranslation } from "react-i18next";
import { PagedResults } from "@/shared/components/PagedResults";
import { INVITATIONS_PAGE_SIZE } from "./constants";
import { invitationsErrorKey } from "./invitations-error";
import { InvitationsTable } from "./InvitationsTable";
import type { AdminInvitationsList } from "./types";

interface InvitationsResultsProps {
  /** Prefix of every `data-testid`: `{prefix}-loading|error|retry|empty|last-page|list|pagination`. */
  readonly testIdPrefix: string;
  readonly data: AdminInvitationsList | undefined;
  readonly page: number;
  readonly isLoading: boolean;
  readonly isFetching: boolean;
  readonly isError: boolean;
  readonly error: unknown;
  readonly onRetry: () => void;
  readonly onPageChange: (page: number) => void;
  readonly emptyTitle?: string;
  /** Empty-state copy for a list that is genuinely empty. */
  readonly emptyBody: string;
  /** Empty-state copy when rows vanished and an earlier page still has some. */
  readonly pastEndBody: string;
  readonly hideOrganization?: boolean;
}

/**
 * Loading, error, empty and list states plus the pager of an invitations
 * list, shared by the all-organizations view and the organization page. The
 * error state shows a generic message, never the server's (invitee emails are
 * PII and the read is audited and throttled), and offers no retry on a 403.
 */
export function InvitationsResults({
  data,
  error,
  hideOrganization,
  ...rest
}: InvitationsResultsProps) {
  const { t } = useTranslation();
  const errorKey = invitationsErrorKey(error);
  const items = data?.items ?? [];
  return (
    <PagedResults
      {...rest}
      limit={data?.limit ?? INVITATIONS_PAGE_SIZE}
      totalCount={data?.totalCount ?? 0}
      itemCount={items.length}
      errorMessage={t(errorKey)}
      canRetry={errorKey !== "invitations.errors.forbidden"}
      labels={{
        loading: t("invitations.loading"),
        retry: t("invitations.retry"),
        lastPage: t("invitations.empty.lastPage"),
      }}
    >
      <InvitationsTable items={items} hideOrganization={hideOrganization} />
    </PagedResults>
  );
}
